// CRM → Gestión Comercial → Contratos (/crm/contratos).
// Contratos comerciales con un partner (prospecto o cliente) firmados
// electrónicamente con el .p12 DEL USUARIO que se escoja como firmante:
//   borrador → «Generar y firmar» encola crm.contrato_pdfs → worker notificador
//   arma el PDF, lo firma PKCS#7 con la firma del usuario y lo guarda en BD →
//   descarga + verificación pública por QR (/verificar-contrato-crm/:codigo).
// La firma de cada usuario (.p12 + clave, cifrada) se sube en «Firmas» (admin).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileSignature, KeyRound, Pencil, Plus, Printer, XCircle } from 'lucide-react';
import { abrirContratoBorrador } from './crmContratoPdf';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, Modal, SELECT_PLACEHOLDER } from '@/features/admin/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const CRM = { headers: { 'Content-Profile': 'crm', 'Accept-Profile': 'crm' } };
const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Contrato {
  id: string; codigo: string | null; titulo: string; objeto: string; clausulas: string | null;
  monto: number; moneda: string; forma_pago: string | null;
  fecha_inicio: string; fecha_fin: string | null; estado: string; created_at: string;
  representante_nombre: string | null; representante_identificacion: string | null;
  partner_id: string; cliente: string; cliente_identificacion: string | null; cliente_estado: string;
  firmante_usuario_id: string; firmante: string;
  pdf_estado: string | null; pdf_firmado_at: string | null; pdf_error: string | null;
  codigo_verificacion: string | null;
}
interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface FirmaUsuario {
  usuario_id: string; username: string; email: string | null;
  firmante: string | null; configurada: boolean; activo: boolean; actualizada: string | null;
}

const ESTADO_BADGE: Record<string, string> = {
  borrador: 'bg-muted text-muted-foreground',
  por_firmar: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  firmado: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  anulado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const FORM0 = {
  partner_id: '', titulo: '', objeto: '', clausulas: '', monto: '', moneda: 'USD',
  forma_pago: '', fecha_inicio: '', fecha_fin: '', representante_nombre: '',
  representante_identificacion: '', firmante_usuario_id: '',
};

const usd = (v: number, mon = 'USD') => `${mon === 'USD' ? '$' : `${mon} `}${Number(v || 0).toFixed(2)}`;

// Cláusulas típicas de un contrato de prestación de servicios informáticos (Ecuador).
// Formato «TÍTULO — texto» (una por línea): el PDF las numera solas (CUARTA, QUINTA…).
const CLAUSULAS_SERVICIOS_TI = [
  'CONFIDENCIALIDAD — Las partes guardarán estricta reserva sobre la información técnica, comercial y de negocio a la que accedan con ocasión de este contrato; esta obligación subsistirá por dos (2) años posteriores a su terminación.',
  'PROPIEDAD INTELECTUAL — Los entregables desarrollados a la medida para EL CLIENTE le pertenecen una vez cancelado el precio total. EL PROVEEDOR conserva la titularidad de sus herramientas, librerías y componentes preexistentes, sobre los que otorga a EL CLIENTE una licencia de uso no exclusiva e intransferible.',
  'PROTECCIÓN DE DATOS PERSONALES — El tratamiento de datos personales se sujeta a la Ley Orgánica de Protección de Datos Personales. EL PROVEEDOR actúa como encargado del tratamiento, los usa solo para ejecutar el contrato y los devuelve o elimina a su término.',
  'SOPORTE Y GARANTÍA — EL PROVEEDOR garantiza el correcto funcionamiento de los entregables por noventa (90) días desde su aceptación, corrigiendo sin costo los defectos imputables a su desarrollo. No cubre fallas por mal uso, modificaciones de terceros o cambios del entorno.',
  'NIVELES DE SERVICIO — Cuando se pacte soporte o mantenimiento, los tiempos de respuesta y la disponibilidad se regirán por el Acuerdo de Nivel de Servicio (SLA) que las partes suscriban como anexo.',
  'OBLIGACIONES DEL CLIENTE — EL CLIENTE proporcionará oportunamente la información, accesos, ambientes y contrapartes necesarios. La demora en estas obligaciones extenderá proporcionalmente los plazos.',
  'ACEPTACIÓN DE ENTREGABLES — EL CLIENTE dispondrá de cinco (5) días hábiles para revisar cada entregable; de no formular observaciones por escrito en ese plazo, se entenderá aceptado.',
  'RELACIÓN INDEPENDIENTE — El presente contrato es de naturaleza civil y mercantil; no genera relación laboral ni de dependencia entre las partes ni respecto del personal que cada una asigne.',
  'CESIÓN — Ninguna parte podrá ceder este contrato ni los derechos y obligaciones que de él emanan sin autorización escrita de la otra.',
  'TERMINACIÓN — El contrato podrá terminar por mutuo acuerdo, por cumplimiento de su objeto o por incumplimiento grave no subsanado dentro de los quince (15) días de notificado.',
  'MULTAS — El retraso injustificado imputable a EL PROVEEDOR causará una multa del 0,5% del valor del entregable en mora por cada día de atraso, hasta un máximo del 10% del valor del contrato.',
  'FUERZA MAYOR — Ninguna parte será responsable por incumplimientos derivados de caso fortuito o fuerza mayor debidamente comprobados, mientras dure el evento.',
  'TRIBUTOS — Los valores no incluyen impuestos, los que se facturarán conforme a la ley; cada parte asumirá los tributos que le correspondan.',
  'NOTIFICACIONES — Las comunicaciones se cursarán a los correos y direcciones señalados por las partes, teniéndose por válidas las remitidas a dichos medios.',
  'MODIFICACIONES — Toda modificación a este contrato deberá constar por escrito y suscribirse por las partes.',
  'SOLUCIÓN DE CONTROVERSIAS — Las diferencias que no se resuelvan de mutuo acuerdo se someterán a mediación en un centro autorizado; de subsistir, a la justicia ordinaria del domicilio de EL PROVEEDOR, a cuyo fuero se someten las partes.',
].join('\n');

// Cláusulas típicas de un contrato de prestación de servicios de CATERING / alimentación (Ecuador).
const CLAUSULAS_SERVICIOS_CATERING = [
  'ALCANCE DEL SERVICIO — EL PROVEEDOR brindará el servicio de alimentación conforme a los menús, número de raciones, horarios y sedes acordados; toda variación se coordinará por escrito.',
  'CALIDAD E INOCUIDAD — El servicio se prestará bajo Buenas Prácticas de Manufactura (BPM) y los principios del sistema HACCP, cumpliendo la normativa sanitaria vigente (ARCSA/MSP). Los alimentos serán aptos para el consumo humano.',
  'PERMISOS Y REGISTROS SANITARIOS — EL PROVEEDOR mantendrá vigentes el permiso de funcionamiento, los registros sanitarios que correspondan y los carnés de salud del personal manipulador de alimentos.',
  'PERSONAL Y CAPACITACIÓN — El personal estará capacitado en manipulación de alimentos, uniformado y dotado de equipo de protección. Dicho personal mantiene relación laboral exclusiva con EL PROVEEDOR, sin vínculo alguno con EL CLIENTE.',
  'CADENA DE FRÍO Y TRANSPORTE — EL PROVEEDOR garantizará la conservación de las temperaturas de seguridad y el transporte de los alimentos en condiciones higiénico-sanitarias hasta el punto de servicio.',
  'INSUMOS Y MATERIA PRIMA — Salvo pacto en contrario, EL PROVEEDOR suministrará los insumos, garantizando su calidad, frescura y trazabilidad.',
  'INSTALACIONES Y EQUIPOS — Las partes definirán quién provee cocina, comedor y equipos; su uso, limpieza y mantenimiento se sujetará a lo acordado y a las normas sanitarias.',
  'PROGRAMACIÓN DE RACIONES — EL CLIENTE confirmará el número de comensales con la anticipación pactada. Cuando se acuerde un mínimo garantizado (take-or-pay), se facturará al menos dicho mínimo aunque el consumo real sea menor.',
  'CONTROL, MUESTRAS Y SUPERVISIÓN — EL CLIENTE podrá supervisar el servicio. EL PROVEEDOR conservará contramuestras de cada preparación por al menos setenta y dos (72) horas para eventuales análisis.',
  'RESPONSABILIDAD SANITARIA Y SEGUROS — EL PROVEEDOR responderá por las enfermedades transmitidas por alimentos (ETA) imputables a su servicio y mantendrá las pólizas o seguros que amparen esta responsabilidad.',
  'SEGURIDAD Y SALUD OCUPACIONAL — En las instalaciones de EL CLIENTE, EL PROVEEDOR cumplirá las normas de seguridad y salud en el trabajo y dotará a su personal del equipo de protección requerido.',
  'FACTURACIÓN Y PAGO — Se facturará según las raciones efectivamente servidas o el mínimo garantizado, con la periodicidad y forma de pago acordadas; los valores no incluyen impuestos.',
  'OBLIGACIONES DEL CLIENTE — EL CLIENTE facilitará accesos, servicios básicos, espacios y la información de comensales necesaria; la demora extenderá proporcionalmente los plazos u obligaciones.',
  'PROTECCIÓN DE DATOS PERSONALES — Cuando se traten datos de comensales (padrones, dietas o restricciones), se observará la Ley Orgánica de Protección de Datos Personales, usándolos solo para prestar el servicio.',
  'MULTAS — Los incumplimientos de menús, gramajes, horarios o condiciones de higiene darán lugar a las multas que las partes pacten, sin perjuicio de la reparación de daños.',
  'CONFIDENCIALIDAD — Las partes guardarán reserva sobre la información a la que accedan con ocasión de este contrato, obligación que subsiste tras su terminación.',
  'TERMINACIÓN — El contrato podrá terminar por mutuo acuerdo, por cumplimiento de su objeto o por incumplimiento grave no subsanado dentro de los quince (15) días de notificado.',
  'FUERZA MAYOR — Ninguna parte será responsable por incumplimientos derivados de caso fortuito o fuerza mayor debidamente comprobados, mientras dure el evento.',
  'CESIÓN, NOTIFICACIONES Y MODIFICACIONES — El contrato no podrá cederse sin autorización escrita; las notificaciones se cursarán a los medios señalados por las partes y toda modificación constará por escrito.',
  'SOLUCIÓN DE CONTROVERSIAS — Las diferencias que no se resuelvan de mutuo acuerdo se someterán a mediación en un centro autorizado; de subsistir, a la justicia ordinaria del domicilio de EL PROVEEDOR.',
].join('\n');

export function Contratos() {
  const qc = useQueryClient();
  const [festado, setFestado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState<'lista' | 'form'>('lista');
  const [modalFirmas, setModalFirmas] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...FORM0 });
  const set = (k: keyof typeof FORM0, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dlg = useDialogoEnLinea();

  // firma nueva (modal Firmas)
  const [fUsuario, setFUsuario] = useState('');
  const [fFirmante, setFFirmante] = useState('');
  const [fPassword, setFPassword] = useState('');
  const [fCertB64, setFCertB64] = useState('');
  const [fCertNombre, setFCertNombre] = useState('');

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['crm-contratos', festado],
    queryFn: async () => (await apiClient.post('/rpc/crm_contratos', { _estado: festado, _partner: null }, CRM)).data,
    staleTime: 0, refetchOnMount: 'always',
    refetchInterval: (q) =>
      (q.state.data ?? []).some((c) => c.pdf_estado === 'pendiente' || c.pdf_estado === 'generando') ? 4000 : false,
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'],
    queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const { data: firmas = [] } = useQuery<FirmaUsuario[]>({
    queryKey: ['crm-usuario-firmas'],
    queryFn: async () => (await apiClient.post('/rpc/usuario_firmas_listar', {})).data,
    staleTime: 0, refetchOnMount: 'always',
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['crm-contratos'] });
    qc.invalidateQueries({ queryKey: ['crm-usuario-firmas'] });
    qc.invalidateQueries({ queryKey: ['crm-prospectos'] });
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const data = { ...form, monto: form.monto === '' ? '0' : form.monto };
      return (await apiClient.post('/rpc/crm_contrato_guardar', { _data: data, _id: editId }, CRM)).data;
    },
    onSuccess: () => { invalidar(); setVista('lista'); toast.success(editId ? 'Contrato actualizado' : 'Contrato creado (borrador)'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const generar = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/crm_contrato_generar_pdf', { _contrato_id: id }, CRM)).data,
    onSuccess: () => { invalidar(); toast.success('Encolado: el worker generará y firmará el PDF en segundos'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const anular = useMutation({
    mutationFn: async (id: string) => (await apiClient.post('/rpc/crm_contrato_anular', { _contrato_id: id }, CRM)).data,
    onSuccess: () => { invalidar(); toast.success('Contrato anulado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setFirma = useMutation({
    mutationFn: async () =>
      (await apiClient.post('/rpc/usuario_firma_set', {
        _usuario: fUsuario, _cert_b64: fCertB64 || null, _password: fPassword || null,
        _firmante: fFirmante || null, _activo: true,
      })).data,
    onSuccess: () => {
      invalidar(); setFCertB64(''); setFCertNombre(''); setFPassword(''); setFFirmante(''); setFUsuario('');
      toast.success('Firma del usuario guardada (clave cifrada en reposo)');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const descargar = async (c: Contrato) => {
    try {
      const d = (await apiClient.post('/rpc/crm_contrato_pdf', { _contrato_id: c.id }, CRM)).data as
        { pdf: string; filename: string } | null;
      if (!d?.pdf) { toast.error('El PDF firmado aún no está disponible'); return; }
      const bytes = Uint8Array.from(atob(d.pdf), (ch) => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = d.filename || 'contrato.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const borrador = async (id: string) => {
    try { await abrirContratoBorrador(id); } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const leerP12 = (file: File | null) => {
    if (!file) { setFCertB64(''); setFCertNombre(''); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || '');
      setFCertB64(res.slice(res.indexOf('base64,') + 7));
      setFCertNombre(file.name);
    };
    reader.readAsDataURL(file);
  };

  const abrirNuevo = () => { setEditId(null); setForm({ ...FORM0 }); setVista('form'); };
  const abrirEditar = (c: Contrato) => {
    setEditId(c.id);
    setForm({
      partner_id: c.partner_id, titulo: c.titulo, objeto: c.objeto, clausulas: c.clausulas ?? '',
      monto: String(c.monto ?? ''), moneda: c.moneda, forma_pago: c.forma_pago ?? '',
      fecha_inicio: c.fecha_inicio?.slice(0, 10) ?? '', fecha_fin: c.fecha_fin?.slice(0, 10) ?? '',
      representante_nombre: c.representante_nombre ?? '',
      representante_identificacion: c.representante_identificacion ?? '',
      firmante_usuario_id: c.firmante_usuario_id,
    });
    setVista('form');
  };

  const filtrados = useMemo(() => contratos.filter((c) => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return `${c.codigo ?? ''} ${c.titulo} ${c.cliente} ${c.firmante}`.toLowerCase().includes(q);
  }), [contratos, busqueda]);

  const getVal = useCallback((c: Contrato, key: string): string | number | null => {
    switch (key) {
      case 'codigo': return c.codigo;
      case 'titulo': return c.titulo;
      case 'cliente': return c.cliente;
      case 'monto': return Number(c.monto);
      case 'inicio': return c.fecha_inicio;
      case 'firmante': return c.firmante;
      case 'estado': return c.estado;
      default: return null;
    }
  }, []);
  const sort = useSort(filtrados, getVal);

  const firmaDe = (usuarioId: string) => firmas.find((f) => f.usuario_id === usuarioId);

  if (vista === 'form') {
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => setVista('lista')} title="Volver al listado">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <FileSignature className="w-5 h-5" /> {editId ? 'Editar contrato' : 'Nuevo contrato'}
          </h1>
        </div>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Datos del contrato</CardTitle>
            <p className="text-xs text-muted-foreground">
              El PDF se firmará electrónicamente con el .p12 del usuario firmante que escojas («Generar y firmar» desde el listado).
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Campo label="Cliente / prospecto *" className="md:col-span-2">
                <select className={fieldCls} value={form.partner_id} onChange={(e) => set('partner_id', e.target.value)}>
                  <option value="">{SELECT_PLACEHOLDER}</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>
                  ))}
                </select>
              </Campo>
              <Campo label="Firmante (usuario del ERP) *">
                <select className={fieldCls} value={form.firmante_usuario_id} onChange={(e) => set('firmante_usuario_id', e.target.value)}>
                  <option value="">{SELECT_PLACEHOLDER}</option>
                  {firmas.map((f) => (
                    <option key={f.usuario_id} value={f.usuario_id}>
                      {f.username}{f.configurada ? ' ✓ firma' : ' (sin firma)'}
                    </option>
                  ))}
                </select>
              </Campo>
              {form.firmante_usuario_id && !firmaDe(form.firmante_usuario_id)?.configurada && (
                <p className="md:col-span-3 text-xs text-amber-600 dark:text-amber-400">
                  Este usuario aún no tiene firma electrónica: súbela en «Firmas de usuarios» antes de generar el PDF.
                </p>
              )}
              <Campo label="Título del contrato *" className="md:col-span-3">
                <input className={fieldCls} maxLength={200} placeholder="p.ej. Contrato de prestación de servicios"
                       value={form.titulo} onChange={(e) => set('titulo', e.target.value)} />
              </Campo>
              <Campo label="Objeto del contrato *" className="md:col-span-3">
                <textarea className={fieldCls} rows={4}
                          placeholder="Qué se contrata: alcance, entregables, condiciones esenciales…"
                          value={form.objeto} onChange={(e) => set('objeto', e.target.value)} />
              </Campo>
              <Campo label="Cláusulas adicionales (una por párrafo; opcional «TÍTULO: texto»)" className="md:col-span-3">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-medium text-muted-foreground">Cláusulas típicas:</span>
                  <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-muted"
                          onClick={() => setForm((f) => ({ ...f, clausulas: (f.clausulas.trim() ? `${f.clausulas.trim()}\n` : '') + CLAUSULAS_SERVICIOS_TI }))}>
                    + Servicios informáticos
                  </button>
                  <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-muted"
                          onClick={() => setForm((f) => ({ ...f, clausulas: (f.clausulas.trim() ? `${f.clausulas.trim()}\n` : '') + CLAUSULAS_SERVICIOS_CATERING }))}>
                    + Catering / alimentación
                  </button>
                  {form.clausulas.trim() && (
                    <button type="button" className="rounded-md border px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                            onClick={() => set('clausulas', '')}>Limpiar</button>
                  )}
                  <span className="w-full text-[11px] text-muted-foreground">Se agregan al final; edítalas o quita las que no apliquen.</span>
                </div>
                <textarea className={fieldCls} rows={9}
                          placeholder={'CONFIDENCIALIDAD: Las partes guardarán reserva…\nGARANTÍA: El servicio tendrá una garantía de…'}
                          value={form.clausulas} onChange={(e) => set('clausulas', e.target.value)} />
              </Campo>
              <Campo label="Monto">
                <input className={fieldCls} type="number" min="0" step="0.01" value={form.monto}
                       onChange={(e) => set('monto', e.target.value)} />
              </Campo>
              <Campo label="Moneda">
                <input className={fieldCls} maxLength={8} value={form.moneda} onChange={(e) => set('moneda', e.target.value)} />
              </Campo>
              <Campo label="Forma de pago">
                <input className={fieldCls} placeholder="p.ej. 50% anticipo, 50% contra entrega"
                       value={form.forma_pago} onChange={(e) => set('forma_pago', e.target.value)} />
              </Campo>
              <Campo label="Fecha de inicio">
                <input className={fieldCls} type="date" value={form.fecha_inicio} onChange={(e) => set('fecha_inicio', e.target.value)} />
              </Campo>
              <Campo label="Fecha de fin (opcional)">
                <input className={fieldCls} type="date" value={form.fecha_fin} onChange={(e) => set('fecha_fin', e.target.value)} />
              </Campo>
              <div />
              <Campo label="Representante del cliente (opcional)">
                <input className={fieldCls} value={form.representante_nombre}
                       onChange={(e) => set('representante_nombre', e.target.value)} />
              </Campo>
              <Campo label="Identificación del representante">
                <input className={fieldCls} maxLength={20} value={form.representante_identificacion}
                       onChange={(e) => set('representante_identificacion', e.target.value)} />
              </Campo>
            </div>
            <div className="flex justify-end gap-2 mt-4 border-t pt-4">
              <Button variant="outline" onClick={() => setVista('lista')}>Cancelar</Button>
              <Button onClick={() => guardar.mutate()} isLoading={guardar.isPending}
                      disabled={!form.partner_id || !form.firmante_usuario_id || !form.titulo.trim() || !form.objeto.trim()}>
                {editId ? 'Guardar cambios' : 'Crear contrato'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <FileSignature className="w-5 h-5" /> Contratos
        </h1>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setModalFirmas(true)}>
            <KeyRound className="w-4 h-4 mr-1" /> Firmas de usuarios
          </Button>
          <Button size="sm" onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo contrato</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-base">Contratos comerciales con firma electrónica</CardTitle>
            <select className={`${fieldCls} !w-auto`} value={festado} onChange={(e) => setFestado(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="borrador">Borradores</option>
              <option value="por_firmar">Por firmar</option>
              <option value="firmado">Firmados</option>
              <option value="anulado">Anulados</option>
            </select>
            <input className={`${fieldCls} !w-64`} placeholder="Buscar (código, título, cliente…)"
                   value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <SortTh col="codigo" sort={sort}>Código</SortTh>
                  <SortTh col="titulo" sort={sort}>Título</SortTh>
                  <SortTh col="cliente" sort={sort}>Cliente / prospecto</SortTh>
                  <SortTh col="monto" sort={sort} align="right">Monto</SortTh>
                  <SortTh col="inicio" sort={sort}>Inicio</SortTh>
                  <SortTh col="firmante" sort={sort}>Firmante</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((c, i) => (
                  <tr key={c.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-mono whitespace-nowrap">{c.codigo}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{c.titulo}</div>
                      {c.pdf_estado === 'error' && (
                        <div className="text-xs text-red-600 dark:text-red-400">Error al firmar: {c.pdf_error}</div>
                      )}
                      {(c.pdf_estado === 'pendiente' || c.pdf_estado === 'generando') && (
                        <div className="text-xs text-amber-600 dark:text-amber-400">Generando y firmando…</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div>{c.cliente}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.cliente_estado === 'prospecto' ? 'prospecto' : ''} {c.cliente_identificacion ?? ''}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{usd(c.monto, c.moneda)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_inicio?.slice(0, 10)}</td>
                    <td className="py-2 pr-3">{c.firmante}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ESTADO_BADGE[c.estado] ?? ''}`}>
                        {c.estado.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {(c.estado === 'borrador' || c.estado === 'por_firmar') && (
                        <>
                          <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirEditar(c)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Imprimir borrador (sin firma) para revisión" onClick={() => void borrador(c.id)}>
                            <Printer className="w-4 h-4 mr-1" /> Borrador
                          </Button>
                          <Button size="sm" variant="outline" disabled={generar.isPending || c.pdf_estado === 'generando' || c.pdf_estado === 'pendiente'}
                                  title="Generar el PDF y firmarlo con el .p12 del firmante"
                                  onClick={() => generar.mutate(c.id)}>
                            <FileSignature className="w-4 h-4 mr-1" /> Generar y firmar
                          </Button>
                        </>
                      )}
                      {c.pdf_estado === 'firmado' && (
                        <Button size="sm" variant="outline" title="Descargar PDF firmado" onClick={() => void descargar(c)}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      )}
                      {c.estado !== 'anulado' && (
                        <Button size="sm" variant="ghost" title="Anular contrato"
                                onClick={async () => { if (await dlg.confirmar(`¿Anular el contrato ${c.codigo}?`, { peligro: true })) anular.mutate(c.id); }}>
                          <XCircle className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!sort.sorted.length && (
                  <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">
                    Sin contratos. Crea uno con «Nuevo contrato» y fírmalo con la firma electrónica del usuario que escojas.
                  </td></tr>
                )}
                {sort.sorted.length > 0 && (
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={4}>Totales ({sort.sorted.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {usd(sort.sorted.reduce((a, c) => a + Number(c.monto), 0))}
                    </td>
                    <td colSpan={4} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground border-t pt-2 mt-2">
            El PDF se firma electrónicamente (PKCS#7) con el certificado .p12 del usuario firmante e incluye un QR de
            verificación pública. Los firmados ya no se editan: para cambiar algo, anula y crea uno nuevo.
          </p>
        </CardContent>
      </Card>

      {modalFirmas && (
        <Modal title="Firmas electrónicas de usuarios (.p12)" onClose={() => setModalFirmas(false)} wide>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Sube el certificado .p12 de cada usuario que firmará contratos. La clave se guarda <span className="font-medium">cifrada</span> y
              nunca se muestra; solo el worker de firma la usa. Gestión exclusiva de administradores.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="py-2 pr-3 text-right w-10">N°</th>
                    <th className="py-2 pr-3 text-left">Usuario</th>
                    <th className="py-2 pr-3 text-left">Firmante (sello)</th>
                    <th className="py-2 pr-3 text-center">Firma</th>
                    <th className="py-2 pr-3 text-left">Actualizada</th>
                  </tr>
                </thead>
                <tbody>
                  {firmas.map((f, i) => (
                    <tr key={f.usuario_id} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3">{f.username}{f.email ? ` (${f.email})` : ''}</td>
                      <td className="py-2 pr-3">{f.firmante ?? '—'}</td>
                      <td className="py-2 pr-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${f.configurada
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'}`}>
                          {f.configurada ? 'Configurada' : 'Sin firma'}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">
                        {f.actualizada ? new Date(f.actualizada).toLocaleString(LOCALE, { hour12: false }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border rounded-md p-3 space-y-3">
              <div className="text-sm font-medium">Subir / actualizar firma</div>
              <div className="grid gap-3 md:grid-cols-2">
                <Campo label="Usuario">
                  <select className={fieldCls} value={fUsuario} onChange={(e) => {
                    setFUsuario(e.target.value);
                    setFFirmante(firmaDe(e.target.value)?.firmante ?? '');
                  }}>
                    <option value="">{SELECT_PLACEHOLDER}</option>
                    {firmas.map((f) => <option key={f.usuario_id} value={f.usuario_id}>{f.username}</option>)}
                  </select>
                </Campo>
                <Campo label="Nombre del firmante (aparece en el sello)">
                  <input className={fieldCls} placeholder="p.ej. Ing. Juan Pérez — Gerente Comercial"
                         value={fFirmante} onChange={(e) => setFFirmante(e.target.value)} />
                </Campo>
                <Campo label="Archivo .p12 (vacío = conservar el actual)">
                  <input className={fieldCls} type="file" accept=".p12,.pfx"
                         onChange={(e) => leerP12(e.target.files?.[0] ?? null)} />
                </Campo>
                <Campo label="Clave del certificado (vacía = conservar la actual)">
                  <input className={fieldCls} type="password" autoComplete="new-password"
                         value={fPassword} onChange={(e) => setFPassword(e.target.value)} />
                </Campo>
              </div>
              {fCertNombre && <div className="text-xs text-muted-foreground">Archivo listo: {fCertNombre}</div>}
              <div className="flex justify-end">
                <Button onClick={() => setFirma.mutate()} isLoading={setFirma.isPending}
                        disabled={!fUsuario || (!fCertB64 && !fPassword && !fFirmante)}>
                  Guardar firma
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
